import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent } from '../../../shared/components/ui';
import { buildFeeTrendData } from '../../../shared/services/insightsApi';

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-gray-900 dark:text-gray-100 mb-2">{label}</p>
      <div className="flex items-center gap-2 mb-1">
        <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 bg-violet-500" />
        <span className="text-gray-700 dark:text-gray-200">
          Avg Fee/SF: <span className="font-medium">${payload[0].value?.toFixed(2)}</span>
        </span>
      </div>
      {d?.count != null && (
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{d.count} estimate{d.count !== 1 ? 's' : ''}</p>
      )}
    </div>
  );
}

/**
 * FeeTrendChart — Line chart showing average fee cost per SF over time.
 * The fee category is identified by categoryCode === '00'.
 * Props: estimates (Array) — raw filtered estimates from Insights.jsx
 */
export function FeeTrendChart({ estimates }) {
  const data = useMemo(() => buildFeeTrendData(estimates), [estimates]);

  // Compute overall average for a reference line
  const overall =
    data.length > 0
      ? parseFloat(
          (data.reduce((s, d) => s + d.avgFeeCostPerSf, 0) / data.length).toFixed(2)
        )
      : null;

  return (
    <Card className="shadow-sm">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Avg Fee per SF Over Time</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Monthly average of the fee category cost per square foot
            </p>
          </div>
          {overall != null && (
            <span className="text-xs text-gray-500 dark:text-gray-400 bg-violet-50 dark:bg-violet-900/30 border border-violet-100 dark:border-violet-900/40 px-2.5 py-1 rounded-full">
              Avg ${overall}/SF
            </span>
          )}
        </div>
      </div>

      <CardContent className="pt-4 pb-2">
        {data.length < 2 ? (
          <div className="flex flex-col items-center justify-center h-56 text-center">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M4 19h16a2 2 0 002-2V7a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No fee trend data available</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Estimates with a fee category (code 00) will appear here
            </p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
              />
              <YAxis
                tickFormatter={(v) => `$${v}`}
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={false}
                width={52}
              />
              <Tooltip content={<CustomTooltip />} />
              {overall != null && (
                <ReferenceLine
                  y={overall}
                  stroke="#8b5cf6"
                  strokeDasharray="4 3"
                  strokeOpacity={0.5}
                />
              )}
              <Line
                type="monotone"
                dataKey="avgFeeCostPerSf"
                stroke="#8b5cf6"
                strokeWidth={2.5}
                dot={{ r: 3.5, fill: '#8b5cf6', strokeWidth: 0 }}
                activeDot={{ r: 5, fill: '#8b5cf6' }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

export default FeeTrendChart;
